import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Search, Boxes, AlertTriangle, CheckCircle2, PackageX } from "lucide-react";
import { GlassCard } from "../../components/common/ui";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const INV_BADGE = {
  IN_STOCK: "bg-emerald-500/15 text-emerald-400", LOW_STOCK: "bg-amber/15 text-amber",
  OUT_OF_STOCK: "bg-firered/15 text-firered", COMING_SOON: "bg-sky-500/15 text-sky-400",
  UNVERIFIED: "bg-white/10 text-white/50",
};
const SOURCES = ["MANUAL_STOCKTAKE", "DELIVERY_RECEIPT", "SUPPLIER_IMPORT", "ADMIN_ADJUSTMENT"];
const selectCls = "bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm";
const MANAGERS = ["super_admin", "manager"];

export default function AdminInventory() {
  const { user } = useAuth();
  const canEdit = MANAGERS.includes(user?.role);
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("q") || "");
  const [stDlg, setStDlg] = useState(null);         // stocktake dialog product
  const [form, setForm] = useState({ on_hand_quantity: "", low_stock_threshold: "", source: "MANUAL_STOCKTAKE", note: "" });
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Stock-file import
  const [sfOpen, setSfOpen] = useState(false);
  const [sfStep, setSfStep] = useState("upload");   // upload | map | preview
  const [sfInspect, setSfInspect] = useState(null);
  const [sfMap, setSfMap] = useState({ sku: "", quantity: "", barcode: "", name: "", low_stock_threshold: "" });
  const [sfSource, setSfSource] = useState("DELIVERY_RECEIPT");
  const [sfRef, setSfRef] = useState("");
  const [sfPreview, setSfPreview] = useState(null);
  const [sfBusy, setSfBusy] = useState(false);
  const [batches, setBatches] = useState([]);

  const loadBatches = () => api.get("/admin/inventory/stock-file/batches").then(({ data }) => setBatches(data)).catch(() => {});

  const onSfFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setSfBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/inventory/stock-file/inspect", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSfInspect(data);
      const cols = data.columns || [];
      const guess = (opts) => cols.find((c) => opts.some((o) => c.toLowerCase().includes(o))) || "";
      setSfMap({ sku: guess(["sku", "item"]), quantity: guess(["qty", "quantity", "on hand", "stock"]), barcode: guess(["barcode", "ean"]), name: guess(["name", "product"]), low_stock_threshold: guess(["threshold", "low"]) });
      setSfStep("map");
    } catch { toast.error("Could not read file."); }
    finally { setSfBusy(false); }
  };

  const sfRun = async (dry) => {
    setSfBusy(true);
    try {
      const { data } = await api.post("/admin/inventory/stock-file/commit", {
        rows: sfInspect.rows, mapping: sfMap, source: sfSource, dry_run: dry,
        filename: sfInspect.filename, reference: sfRef || undefined,
      });
      if (dry) { setSfPreview(data); setSfStep("preview"); }
      else { toast.success(`Applied ${data.applied} verified updates.`); setSfOpen(false); resetSf(); load(); loadBatches(); }
    } catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === "string" ? d : d?.message || "Import failed."); }
    finally { setSfBusy(false); }
  };
  const resetSf = () => { setSfStep("upload"); setSfInspect(null); setSfPreview(null); setSfRef(""); };

  const rollback = async (batch_id) => {
    try { const { data } = await api.post(`/admin/inventory/stock-file/batches/${batch_id}/rollback`); toast.success(`Rolled back ${data.rolled_back} products.`); loadBatches(); load(); }
    catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === "string" ? d : d?.message || "Rollback blocked."); }
  };

  const get = (k, d = "") => params.get(k) || d;
  const setParam = (k, v) => { const n = new URLSearchParams(params); v ? n.set(k, v) : n.delete(k); if (k !== "page") n.delete("page"); setParams(n); };

  const load = useCallback(() => {
    setLoading(true);
    const q = { q: get("q"), verified: get("verified"), inventory_status: get("inventory_status"),
      placeholder: get("placeholder"), page: get("page", "1"), page_size: 20 };
    Object.keys(q).forEach((k) => !q[k] && delete q[k]);
    api.get("/admin/inventory", { params: q }).then(({ data }) => setData(data)).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => setParam("q", search), 400); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const openStocktake = (p) => { setForm({ on_hand_quantity: p.inventory_verified ? (p.on_hand_quantity ?? "") : "", low_stock_threshold: p.low_stock_threshold ?? 5, source: "MANUAL_STOCKTAKE", note: "" }); setStDlg(p); };

  const submitStocktake = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/inventory/${stDlg.product_id}/stocktake`, {
        on_hand_quantity: parseInt(form.on_hand_quantity, 10),
        low_stock_threshold: form.low_stock_threshold === "" ? undefined : parseInt(form.low_stock_threshold, 10),
        source: form.source, note: form.note || undefined,
      });
      toast.success("Stock verified & recorded."); setStDlg(null); load();
    } catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === "string" ? d : d?.message || "Could not save."); }
    finally { setSaving(false); }
  };

  const parseBulk = () => bulkText.trim().split("\n").map((ln) => { const [sku, qty] = ln.split(",").map((s) => s.trim()); return { sku, on_hand_quantity: parseInt(qty, 10) }; }).filter((r) => r.sku);
  const runBulk = async (dry) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/inventory/bulk", { rows: parseBulk(), source: "MANUAL_STOCKTAKE", dry_run: dry });
      setBulkPreview(data);
      if (!dry) { toast.success(`Applied ${data.applied} updates.`); setBulkOpen(false); setBulkText(""); setBulkPreview(null); load(); }
    } catch { toast.error("Bulk update failed."); }
    finally { setBulkBusy(false); }
  };

  const s = data?.summary || {};
  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="admin-inventory">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl text-white flex items-center gap-2"><Boxes className="h-7 w-7 text-gold" /> Inventory</h1>
          <p className="mt-1 text-muted-foreground text-sm">Verify genuine physical stock. Seeded quantities are placeholders until verified.</p>
        </div>
        {canEdit && <div className="flex gap-2">
          <Button onClick={() => { setSfOpen(true); resetSf(); loadBatches(); }} className="cf-gold-btn font-semibold" data-testid="stockfile-open-btn">Import Stock File</Button>
          <Button onClick={() => { setBulkOpen(true); setBulkPreview(null); }} variant="outline" className="border-white/20 text-white" data-testid="bulk-open-btn">Bulk Update</Button>
        </div>}
      </div>

      {/* Unverified warning */}
      {s.unverified > 0 && (
        <div className="rounded-xl border border-amber/40 bg-amber/10 p-4 flex items-center gap-3 text-amber text-sm" data-testid="unverified-warning">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span><strong>{s.unverified}</strong> of {s.total} products have unverified placeholder stock. Reservations cannot be confirmed until their quantities are verified.</span>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["total", "Total Products", Boxes], ["verified", "Verified", CheckCircle2], ["unverified", "Unverified", AlertTriangle], ["placeholder", "Placeholder", PackageX]].map(([k, label, Icon]) => (
          <button key={k} data-testid={`inv-summary-${k}`} onClick={() => k === "placeholder" ? setParam("placeholder", get("placeholder") ? "" : "true") : k === "verified" ? setParam("verified", get("verified") === "true" ? "" : "true") : k === "unverified" ? setParam("verified", get("verified") === "false" ? "" : "false") : null}
            className="rounded-xl border border-white/10 bg-white/5 p-4 text-left hover:border-white/25">
            <Icon className="h-4 w-4 text-gold" /><p className="mt-2 font-playfair text-2xl text-white">{s[k] ?? 0}</p><p className="text-xs text-muted-foreground">{label}</p>
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input data-testid="inv-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, SKU, barcode…" className="pl-9 bg-black/30 border-white/15 text-white" /></div>
        <select data-testid="inv-verified-filter" value={get("verified")} onChange={(e) => setParam("verified", e.target.value)} className={selectCls}>
          <option value="">All</option><option value="true">Verified</option><option value="false">Unverified</option>
        </select>
        <select data-testid="inv-status-filter" value={get("inventory_status")} onChange={(e) => setParam("inventory_status", e.target.value)} className={selectCls}>
          <option value="">Any status</option>
          {Object.keys(INV_BADGE).map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
        </select>
      </div>

      {loading ? <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
        : items.length === 0 ? <GlassCard className="p-10 text-center text-muted-foreground" hover={false} data-testid="inv-empty">No products match.</GlassCard>
        : (
          <>
            <div className="overflow-x-auto rounded-xl border border-white/10" data-testid="inv-table">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-white/5 text-white/60 text-xs uppercase"><tr>
                  <th className="text-left p-3">Product</th><th className="text-left p-3">SKU</th>
                  <th className="text-center p-3">On hand</th><th className="text-center p-3">Reserved</th>
                  <th className="text-center p-3">Available</th><th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Action</th></tr></thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.product_id} className="border-t border-white/5" data-testid={`inv-row-${p.product_id}`}>
                      <td className="p-3 text-white/90 flex items-center gap-2">
                        <div className="h-9 w-9 rounded bg-black/30 overflow-hidden shrink-0">{p.image && <img src={p.image} alt="" className="h-full w-full object-cover" />}</div>
                        <span className="truncate max-w-[220px]">{p.name}</span>
                      </td>
                      <td className="p-3 text-white/50">{p.sku || "—"}</td>
                      <td className="p-3 text-center text-white/80">{p.inventory_verified ? p.on_hand_quantity : "—"}</td>
                      <td className="p-3 text-center text-white/60">{p.reserved_quantity}</td>
                      <td className="p-3 text-center text-white">{p.inventory_verified ? p.available_quantity : "—"}</td>
                      <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${INV_BADGE[p.inventory_status]}`}>{p.inventory_status.replace(/_/g, " ")}</span></td>
                      <td className="p-3 text-right">{canEdit && <Button size="sm" onClick={() => openStocktake(p)} data-testid={`verify-btn-${p.product_id}`} className="cf-gold-btn font-semibold h-8">{p.inventory_verified ? "Update" : "Verify"}</Button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.pages > 1 && (
              <div className="flex items-center justify-center gap-3" data-testid="inv-pagination">
                <Button variant="outline" className="border-white/20 text-white" disabled={data.page <= 1} onClick={() => setParam("page", String(data.page - 1))}>Previous</Button>
                <span className="text-sm text-muted-foreground">Page {data.page} of {data.pages} · {data.total} total</span>
                <Button variant="outline" className="border-white/20 text-white" disabled={data.page >= data.pages} onClick={() => setParam("page", String(data.page + 1))}>Next</Button>
              </div>
            )}
          </>
        )}

      {/* Stocktake dialog */}
      <Dialog open={!!stDlg} onOpenChange={(o) => !o && setStDlg(null)}>
        <DialogContent className="bg-navy border-white/15" data-testid="stocktake-dialog">
          <DialogHeader><DialogTitle className="text-white">Verify stock — {stDlg?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-white/70">Physical on-hand quantity</Label><Input data-testid="stocktake-qty" type="number" min={0} value={form.on_hand_quantity} onChange={(e) => setForm({ ...form, on_hand_quantity: e.target.value })} className="mt-1 bg-black/30 border-white/15 text-white" /></div>
            <div><Label className="text-white/70">Low-stock threshold</Label><Input type="number" min={0} value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} className="mt-1 bg-black/30 border-white/15 text-white" /></div>
            <div><Label className="text-white/70">Source</Label>
              <select data-testid="stocktake-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className={`mt-1 w-full ${selectCls}`}>
                {SOURCES.map((x) => <option key={x} value={x}>{x.replace(/_/g, " ")}</option>)}
              </select></div>
            <div><Label className="text-white/70">Note (optional)</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1 bg-black/30 border-white/15 text-white" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-white/60" onClick={() => setStDlg(null)}>Cancel</Button>
            <Button data-testid="stocktake-submit" disabled={saving || form.on_hand_quantity === ""} onClick={submitStocktake} className="cf-gold-btn font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-navy border-white/15 max-w-lg" data-testid="bulk-dialog">
          <DialogHeader><DialogTitle className="text-white">Bulk stock update</DialogTitle></DialogHeader>
          <p className="text-xs text-white/50">One per line: <code>SKU,quantity</code>. Products are matched by SKU/item number.</p>
          <textarea data-testid="bulk-textarea" value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={"CF-ABC-001,24\nCF-XYZ-009,0"} className="w-full bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm min-h-[120px] font-mono" />
          {bulkPreview && (
            <div className="max-h-40 overflow-y-auto text-xs border border-white/10 rounded-md" data-testid="bulk-preview">
              {bulkPreview.rows.map((r, i) => (
                <div key={i} className={`flex justify-between px-3 py-1.5 border-b border-white/5 ${r.status === "error" ? "text-firered" : "text-white/70"}`}>
                  <span>{r.sku} {r.name ? `· ${r.name}` : ""}</span>
                  <span>{r.status === "error" ? r.message : `${r.current_on_hand} → ${r.new_on_hand}`}</span>
                </div>
              ))}
              <div className="px-3 py-1.5 text-white/50">OK: {bulkPreview.ok} · Errors: {bulkPreview.errors}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-white/20 text-white" disabled={bulkBusy || !bulkText.trim()} onClick={() => runBulk(true)} data-testid="bulk-preview-btn">Preview</Button>
            <Button className="cf-gold-btn font-semibold" disabled={bulkBusy || !bulkPreview || bulkPreview.ok === 0} onClick={() => runBulk(false)} data-testid="bulk-apply-btn">{bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Apply ${bulkPreview?.ok || 0}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock-file import dialog */}
      <Dialog open={sfOpen} onOpenChange={(o) => { setSfOpen(o); if (!o) resetSf(); }}>
        <DialogContent className="bg-navy border-white/15 max-w-2xl" data-testid="stockfile-dialog">
          <DialogHeader><DialogTitle className="text-white">Import genuine stock file</DialogTitle></DialogHeader>

          {sfStep === "upload" && (
            <div className="space-y-4">
              <div><Label className="text-white/70">Inventory source</Label>
                <select data-testid="sf-source" value={sfSource} onChange={(e) => setSfSource(e.target.value)} className={`mt-1 w-full ${selectCls}`}>
                  {(sfInspect?.sources || ["MANUAL_STOCKTAKE", "DELIVERY_RECEIPT", "PURCHASE_ORDER_RECEIPT", "STOCK_CORRECTION"]).map((x) => <option key={x} value={x}>{x.replace(/_/g, " ")}</option>)}
                </select></div>
              <div><Label className="text-white/70">Delivery-note / PO reference (optional)</Label>
                <Input value={sfRef} onChange={(e) => setSfRef(e.target.value)} className="mt-1 bg-black/30 border-white/15 text-white" data-testid="sf-reference" /></div>
              <div>
                <Label className="text-white/70">CSV or Excel file</Label>
                <input type="file" accept=".csv,.xlsx,.xls" onChange={onSfFile} data-testid="sf-file-input"
                  className="mt-1 block w-full text-sm text-white/70 file:mr-3 file:rounded-md file:border-0 file:bg-gold file:text-ink file:px-4 file:py-2 file:font-semibold" />
                <p className="mt-2 text-xs text-white/40">Columns: SKU/item number + genuine on-hand quantity (barcode, name, threshold, notes optional).</p>
              </div>
              {sfBusy && <Loader2 className="h-5 w-5 animate-spin text-gold" />}
              {batches.length > 0 && (
                <div className="border-t border-white/10 pt-3" data-testid="sf-batches">
                  <p className="text-xs text-white/50 mb-2">Recent stock batches</p>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {batches.map((b) => (
                      <div key={b.batch_id} className="flex items-center justify-between text-xs text-white/70">
                        <span>{(b.at || "").slice(0, 16).replace("T", " ")} · {b.source} · {b.count} items {b.reference ? `· ${b.reference}` : ""}</span>
                        {b.rolled_back ? <span className="text-white/30">rolled back</span>
                          : <button onClick={() => rollback(b.batch_id)} data-testid={`rollback-${b.batch_id}`} className="text-firered hover:underline">Rollback</button>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {sfStep === "map" && sfInspect && (
            <div className="space-y-3">
              <p className="text-xs text-white/50">{sfInspect.row_count} rows detected. Map your columns:</p>
              {[["sku", "SKU / item no"], ["quantity", "On-hand quantity *"], ["barcode", "Barcode"], ["name", "Product name"], ["low_stock_threshold", "Low-stock threshold"]].map(([k, label]) => (
                <div key={k} className="flex items-center gap-3">
                  <Label className="text-white/70 w-40 text-sm">{label}</Label>
                  <select data-testid={`sf-map-${k}`} value={sfMap[k]} onChange={(e) => setSfMap({ ...sfMap, [k]: e.target.value })} className={`flex-1 ${selectCls}`}>
                    <option value="">— none —</option>
                    {sfInspect.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}
              <DialogFooter>
                <Button variant="ghost" className="text-white/60" onClick={() => setSfStep("upload")}>Back</Button>
                <Button className="cf-gold-btn font-semibold" data-testid="sf-preview-btn" disabled={sfBusy || !sfMap.quantity || (!sfMap.sku && !sfMap.barcode)} onClick={() => sfRun(true)}>{sfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preview"}</Button>
              </DialogFooter>
            </div>
          )}

          {sfStep === "preview" && sfPreview && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center text-xs" data-testid="sf-summary">
                {[["valid", "Valid"], ["no_change", "No change"], ["not_found", "Not found"], ["conflict", "Conflicts"], ["invalid", "Invalid"], ["becoming_verified", "→ Verified"], ["becoming_low", "→ Low"], ["becoming_out", "→ Out"], ["total", "Total"]].map(([k, l]) => (
                  <div key={k} className="rounded-lg bg-white/5 border border-white/10 py-2"><p className="text-white font-semibold">{sfPreview.summary[k] ?? 0}</p><p className="text-white/40">{l}</p></div>
                ))}
              </div>
              <div className="max-h-52 overflow-y-auto text-xs border border-white/10 rounded-md">
                {sfPreview.rows.map((r, i) => (
                  <div key={i} className={`flex justify-between px-3 py-1.5 border-b border-white/5 ${["not_found", "invalid_quantity", "conflict", "reserved_conflict", "duplicate"].includes(r.result) ? "text-firered" : r.result === "no_change" ? "text-white/40" : "text-white/80"}`}>
                    <span className="truncate">{r.sku || r.barcode} {r.name ? `· ${r.name}` : ""}{r.warning ? " ⚠" : ""}</span>
                    <span className="whitespace-nowrap ml-2">{r.result === "valid_update" ? `${r.current_on_hand}→${r.new_on_hand} (${r.projected_status})` : r.result.replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="ghost" className="text-white/60" onClick={() => setSfStep("map")}>Back</Button>
                <Button className="cf-gold-btn font-semibold" data-testid="sf-apply-btn" disabled={sfBusy || (sfPreview.summary.valid ?? 0) === 0} onClick={() => sfRun(false)}>{sfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirm & Verify ${sfPreview.summary.valid || 0}`}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
